import React, { useMemo, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Clock, TrendingUp, Activity, CheckCircle, Eye, EyeOff } from "lucide-react";
import { cn } from "../../utils/cn";
import { laneColors } from "../../utils/constants";
import { SortableEpicCard } from "../cards/SortableEpicCard";
import { Epic, Project, EpicLane, User } from "../../types";

interface RoadmapViewProps {
    epics: Epic[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    getPreviousScore: (id: string) => number | undefined | null;
    getProjectName?: (id?: string) => string;
    projects?: Project[];
    onUpdate?: (id: string, updates: Partial<Epic>) => void;
    users?: User[];
    userRole?: any;
}

interface RoadmapLaneProps extends Omit<RoadmapViewProps, 'epics'> {
    lane: EpicLane;
    laneEpics: Epic[];
    icon: React.ReactNode;
}

function RoadmapLane({ lane, laneEpics, icon, ...props }: RoadmapLaneProps) {
    const { setNodeRef } = useDroppable({
        id: lane,
    });

    return (
        <div ref={setNodeRef} className="relative pt-8">
            {/* Lane Header */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 z-20 p-2 bg-siemens-stone dark:bg-slate-950">
                <div className={cn(
                    "px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest border-2 flex items-center gap-2 shadow-lg",
                    laneColors[lane].border, laneColors[lane].bg, laneColors[lane].text
                )}>
                    {icon}
                    <span>{lane}</span>
                    <span className="bg-white/50 dark:bg-black/30 px-1.5 py-0.5 rounded-full text-[10px]">
                        {laneEpics.length}
                    </span>
                </div>
            </div>

            {/* Droppable Lane Area */}
            <div className="space-y-4 mt-10 min-h-[200px] p-3 rounded-2xl border-2 border-dashed border-transparent hover:border-slate-200 dark:hover:border-slate-700 transition-colors">
                <SortableContext
                    id={lane}
                    items={laneEpics.map(e => e.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {laneEpics.map(epic => (
                        <SortableEpicCard
                            key={epic.id}
                            epic={epic}
                            {...props}
                        />
                    ))}
                </SortableContext>

                {laneEpics.length === 0 && (
                    <div className="p-8 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl flex flex-col items-center justify-center opacity-40 hover:opacity-60 transition-opacity">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Drop epics here</span>
                        <span className="text-[9px] text-slate-400 mt-1">Drag from other lanes</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export function RoadmapView({
    epics,
    selectedId,
    onSelect,
    getPreviousScore,
    getProjectName,
    projects,
    onUpdate,
    users,
    userRole
}: RoadmapViewProps) {
    // Done lane hidden by default
    const [showDone, setShowDone] = useState(false);

    const groups = useMemo(() => {
        const g: Record<string, Epic[]> = { Now: [], Next: [], Later: [], Done: [] };
        epics.forEach(e => {
            if (g[e.lane]) g[e.lane].push(e);
        });
        return g;
    }, [epics]);

    // Lane icons mapping
    const laneIcons: Record<string, React.ReactNode> = {
        Now: <Clock className="w-3 h-3" />,
        Next: <TrendingUp className="w-3 h-3" />,
        Later: <Activity className="w-3 h-3" />,
        Done: <CheckCircle className="w-3 h-3" />
    };

    const visibleLanes: EpicLane[] = showDone
        ? ["Now", "Next", "Later", "Done"]
        : ["Now", "Next", "Later"];

    return (
        <div className="flex flex-col h-full overflow-y-auto">
            {/* Toggle Bar */}
            <div className="flex items-center gap-2 px-4 md:px-10 pt-4 md:pt-6 pb-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Toggle:</span>
                <button
                    onClick={() => setShowDone(!showDone)}
                    className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide transition-all",
                        !showDone
                            ? "bg-slate-200 dark:bg-slate-800 text-slate-500 hover:bg-slate-300 dark:hover:bg-slate-700"
                            : "bg-siemens-petrol text-white"
                    )}
                >
                    {!showDone ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    Done
                    {groups.Done?.length > 0 && (
                        <span className="ml-1 bg-white/20 px-1.5 py-0.5 rounded-full">{groups.Done.length}</span>
                    )}
                </button>
            </div>

            {/* Roadmap Content */}
            <div className="flex-1 p-4 md:px-10 md:pb-10">
                <div className={cn(
                    "grid grid-cols-1 gap-4 md:gap-6 relative min-h-[400px] md:min-h-[600px]",
                    showDone ? "md:grid-cols-4" : "md:grid-cols-3"
                )}>
                    {/* Timeline Line - hidden on mobile */}
                    <div className="hidden md:block absolute top-[40px] left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-amber-500 via-slate-400 to-green-500 rounded-full opacity-40" />

                    {visibleLanes.map(lane => (
                        <RoadmapLane
                            key={lane}
                            lane={lane}
                            laneEpics={groups[lane] || []}
                            icon={laneIcons[lane]}
                            selectedId={selectedId}
                            onSelect={onSelect}
                            getPreviousScore={getPreviousScore}
                            getProjectName={getProjectName}
                            projects={projects}
                            onUpdate={onUpdate}
                            users={users}
                            userRole={userRole}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default RoadmapView;

